/**
 * Canvas drawing primitives shared by every widget.
 *
 * Chrome is deliberately recessive -- solid hairline grid one shade off the
 * surface, no dashes, no boxes around marks -- so the data is the only loud
 * thing on screen.
 */
import type { RingBuffer, SignalDef, SignalState } from '@obd/core';
import { theme } from './render';

/** Reused so the paint loop never allocates. Sized for the largest window. */
const scratchT = new Float64Array(4096);
const scratchV = new Float64Array(4096);

export function roundRect(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

export function formatValue(value: number | undefined, def: SignalDef): string {
  if (value === undefined || !Number.isFinite(value)) return '--';
  if (def.enumLabels) {
    const label = def.enumLabels[Math.round(value)];
    if (label) return label;
  }
  const abs = Math.abs(value);
  // Large numbers do not need the decimals their definition asks for.
  const decimals = abs >= 1000 ? 0 : def.decimals;
  return value.toFixed(decimals);
}

/** Which status colour, if any, a value has reached. */
export function statusFor(value: number | undefined, def: SignalDef): string | null {
  if (value === undefined) return null;
  if (def.warnAbove !== undefined && value >= def.warnAbove) {
    return value >= def.warnAbove * 1.08 ? theme.get('--critical', '#d03b3b') : theme.get('--warning', '#fab219');
  }
  if (def.warnBelow !== undefined && value <= def.warnBelow) {
    return value <= def.warnBelow * 0.85 ? theme.get('--critical', '#d03b3b') : theme.get('--warning', '#fab219');
  }
  return null;
}

/** Range to draw a signal over: explicit override, else its definition hints. */
export function rangeFor(def: SignalDef, min?: number, max?: number): [number, number] {
  const lo = min ?? def.min;
  const hi = max ?? def.max;
  return hi > lo ? [lo, hi] : [lo, lo + 1];
}

// ------------------------------------------------------------------ ticks

/** Round tick steps (1, 2, 2.5, 5, 10 x powers of ten) covering [min, max]. */
export function niceTicks(min: number, max: number, target = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [min];
  const span = max - min;
  const rawStep = span / target;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = (norm > 5 ? 10 : norm > 2.5 ? 5 : norm > 2 ? 2.5 : norm > 1 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 0.001; v += step) {
    out.push(Math.abs(v) < step * 1e-6 ? 0 : v);
  }
  return out;
}

// -------------------------------------------------------------- sparkline

export interface SparkOptions {
  points: number;
  color: string;
  /** Fill under the line at low alpha. */
  fill?: boolean;
  /** Force the y range instead of autoscaling to the window. */
  range?: [number, number];
}

/**
 * Compact history trace. Autoscales to the visible window by default, which is
 * what makes small movements legible at this size -- the axis is implied, and
 * the number beside it carries the absolute value.
 */
export function drawSparkline(
  ctx: CanvasRenderingContext2D,
  history: RingBuffer,
  x: number, y: number, w: number, h: number,
  opts: SparkOptions,
): void {
  const n = history.copyLast(Math.min(opts.points, scratchV.length), scratchT, scratchV);
  if (n < 2) return;

  let lo: number;
  let hi: number;
  if (opts.range) {
    [lo, hi] = opts.range;
  } else {
    lo = Infinity;
    hi = -Infinity;
    for (let i = 0; i < n; i++) {
      const v = scratchV[i] ?? 0;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    // A perfectly flat trace should sit in the middle, not fill the box.
    if (hi - lo < 1e-9) {
      const pad = Math.max(Math.abs(hi) * 0.05, 0.5);
      lo -= pad;
      hi += pad;
    } else {
      const pad = (hi - lo) * 0.12;
      lo -= pad;
      hi += pad;
    }
  }

  const t0 = scratchT[0] ?? 0;
  const t1 = scratchT[n - 1] ?? t0 + 1;
  const span = Math.max(t1 - t0, 1);
  const px = (i: number): number => x + ((((scratchT[i] ?? 0) - t0) / span) * w);
  const py = (v: number): number => y + h - ((v - lo) / (hi - lo)) * h;

  if (opts.fill) {
    ctx.beginPath();
    ctx.moveTo(px(0), y + h);
    for (let i = 0; i < n; i++) ctx.lineTo(px(i), py(scratchV[i] ?? 0));
    ctx.lineTo(px(n - 1), y + h);
    ctx.closePath();
    ctx.globalAlpha = 0.14;
    ctx.fillStyle = opts.color;
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const cx = px(i);
    const cy = py(scratchV[i] ?? 0);
    if (i === 0) ctx.moveTo(cx, cy);
    else ctx.lineTo(cx, cy);
  }
  ctx.strokeStyle = opts.color;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();

  // Anchor the eye at the newest sample.
  const lastX = px(n - 1);
  const lastY = py(scratchV[n - 1] ?? 0);
  ctx.beginPath();
  ctx.arc(lastX, lastY, 2, 0, Math.PI * 2);
  ctx.fillStyle = opts.color;
  ctx.fill();
}

// ------------------------------------------------------------- line panel

export interface PanelBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface TimeWindow {
  /** Epoch ms at the left edge. */
  t0: number;
  /** Epoch ms at the right edge. */
  t1: number;
}

/**
 * One series panel with its own y-axis.
 *
 * Multi-signal charts stack these rather than overlaying two y-scales on one
 * plot: an overlaid second scale is arbitrary, so it invents a correlation the
 * data does not contain. Stacked panels share the time axis, which is the
 * comparison that is actually meaningful.
 */
export function drawSeriesPanel(
  ctx: CanvasRenderingContext2D,
  state: SignalState,
  bounds: PanelBounds,
  win: TimeWindow,
  color: string,
  opts: { showAxis?: boolean; maxPoints?: number } = {},
): { lo: number; hi: number } | null {
  const { x, y, w, h } = bounds;
  const n = state.history.copyLast(Math.min(opts.maxPoints ?? 2000, scratchV.length), scratchT, scratchV);
  if (n < 2) return null;

  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < n; i++) {
    if ((scratchT[i] ?? 0) < win.t0) continue;
    const v = scratchV[i] ?? 0;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (hi - lo < 1e-9) {
    const pad = Math.max(Math.abs(hi) * 0.1, 1);
    lo -= pad;
    hi += pad;
  } else {
    const pad = (hi - lo) * 0.1;
    lo -= pad;
    hi += pad;
  }

  const span = Math.max(win.t1 - win.t0, 1);
  const px = (t: number): number => x + ((t - win.t0) / span) * w;
  const py = (v: number): number => y + h - ((v - lo) / (hi - lo)) * h;

  // Recessive gridlines: solid hairlines, one shade off the surface.
  const grid = theme.get('--grid', '#2c2c2a');
  const muted = theme.get('--ink-muted', '#898781');
  ctx.strokeStyle = grid;
  ctx.lineWidth = 1;
  const ticks = niceTicks(lo, hi, 2);
  ctx.font = '10px ' + FONT;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (const tick of ticks) {
    const ty = Math.round(py(tick)) + 0.5;
    if (ty < y - 1 || ty > y + h + 1) continue;
    ctx.beginPath();
    ctx.moveTo(x, ty);
    ctx.lineTo(x + w, ty);
    ctx.stroke();
    if (opts.showAxis !== false) {
      ctx.fillStyle = muted;
      ctx.fillText(shortNum(tick), x - 5, ty);
    }
  }

  ctx.beginPath();
  let started = false;
  for (let i = 0; i < n; i++) {
    const t = scratchT[i] ?? 0;
    if (t < win.t0) continue;
    const cx = px(t);
    const cy = py(scratchV[i] ?? 0);
    if (!started) {
      ctx.moveTo(cx, cy);
      started = true;
    } else {
      ctx.lineTo(cx, cy);
    }
  }
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();

  return { lo, hi };
}

/** Value of a series at a given time, for the crosshair tooltip. */
export function valueAtTime(history: RingBuffer, t: number): number | undefined {
  const n = history.length;
  if (n === 0) return undefined;
  // Samples are appended in time order, so a backward scan finds it quickly.
  let best: number | undefined;
  let bestDt = Infinity;
  for (let i = n - 1; i >= 0; i--) {
    const dt = Math.abs(history.timeAt(i) - t);
    if (dt < bestDt) {
      bestDt = dt;
      best = history.valueAt(i);
    } else if (history.timeAt(i) < t - bestDt) {
      break;
    }
  }
  return best;
}

export const FONT = "system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Compact axis labels: 1.2k rather than 1200. */
export function shortNum(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 100000) return (v / 1000).toFixed(0) + 'k';
  if (abs >= 10000) return (v / 1000).toFixed(1) + 'k';
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(abs % 1 === 0 ? 0 : 1);
  if (abs === 0) return '0';
  return v.toFixed(abs < 1 ? 2 : 1);
}

/** Draw text truncated with an ellipsis to fit `maxWidth`. */
export function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(out + '…').width > maxWidth) out = out.slice(0, -1);
  return out + '…';
}
